import React, { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import {
  ClockStopwatch,
  Clock,
  GitBranch01,
  Hand,
  LogOut01,
  Mail01,
  MessageSquare01,
  Stars01,
  Tool01,
} from "@opendate-app/setlist";

import { useCustomCampaignShowContext } from "../CustomCampaignShowContainer";
import NodeErrorContainer from "../NodeErrorContainer";
import TriggerNodeBody from "./TriggerNodeBody";
import TimeDelayNodeBody from "./TimeDelayNodeBody";
import WaitUntilNodeBody from "./WaitUntilNodeBody";
import SendBetweenNodeBody from "./SendBetweenNodeBody";
import EmailNodeBody from "./EmailNodeBody";
import TrueFalseBranchNodeBody from "./TrueFalseBranchNodeBody";
import SmsNodeBody from "./SmsNodeBody";

const NODE_ICONS = {
  "WorkflowNodes::TriggerNode": Stars01,
  "WorkflowNodes::EmailNode": Mail01,
  "WorkflowNodes::SmsNode": MessageSquare01,
  "WorkflowNodes::ManualSegmentUpdateNode": Tool01,
  "WorkflowNodes::TimeDelayNode": ClockStopwatch,
  "WorkflowNodes::SendBetweenNode": Clock,
  "WorkflowNodes::WaitUntilNode": Hand,
  "WorkflowNodes::TrueFalseBranchNode": GitBranch01,
  "WorkflowNodes::ExitNode": LogOut01,
};

const OUTLINE_TOKENS = {
  "#D5D7DA": "var(--sl-border-primary)",
  "#B2DBF5": "var(--sl-border-brand)",
};

const nodeStyle = {
  backgroundColor: "var(--sl-bg-primary)",
  width: "fit-content",
  height: "fit-content",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: "6px 12px",
  color: "var(--sl-text-primary)",
  borderRadius: 4,
};

const headerStyle = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  width: "100%",
};

const titleStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 14,
  lineHeight: "20px",
  fontWeight: 600,
  color: "var(--sl-text-primary)",
};

const iconStyle = {
  flex: "0 0 auto",
  color: "var(--sl-fg-quinary)",
};

const bodyStyle = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  width: "100%",
  marginLeft: 34,
};

export default memo((nodeObject) => {
  const node = nodeObject.data;

  const { handleClick } = node;
  const { selectedNode } = useCustomCampaignShowContext();

  const isSelected = selectedNode?.internal_id === node.internal_id;
  const outlineColor = OUTLINE_TOKENS[node.color] || node.color;
  const Icon = NODE_ICONS[node.type];

  return (
    <div style={{
      ...nodeStyle,
      outline: isSelected ? `2px solid ${outlineColor}` : `1px solid ${outlineColor}`,
      boxShadow: isSelected ? "var(--sl-shadow-sm)" : "none",
    }} onClick={() => handleClick(node)}>
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        style={{visibility: "hidden"}}
      />
      <div style={headerStyle}>
        <div style={titleStyle}>
          {Icon ? <Icon width={16} height={16} style={iconStyle} aria-hidden="true" /> : null}
          {node.title}
        </div>
        <NodeErrorContainer errors={node.workflow_errors} />
      </div>
      <div style={bodyStyle}>
        {
          node.type === "WorkflowNodes::TriggerNode" ? <TriggerNodeBody node={node} isSelected={isSelected} /> :
          node.type === "WorkflowNodes::TimeDelayNode" ? <TimeDelayNodeBody node={node} /> :
          node.type === "WorkflowNodes::WaitUntilNode" ? <WaitUntilNodeBody node={node} /> :
          node.type === "WorkflowNodes::SendBetweenNode" ? <SendBetweenNodeBody node={node} /> :
          node.type === "WorkflowNodes::EmailNode" ? <EmailNodeBody node={node} /> :
          node.type === "WorkflowNodes::SmsNode" ? <SmsNodeBody node={node} /> :
          node.type === "WorkflowNodes::TrueFalseBranchNode" ? <TrueFalseBranchNodeBody node={node} /> :
          null
        }
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        style={{visibility: "hidden"}}
      />
    </div>
  );
});
